import { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, ShieldAlert, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import PremiumVoucherCard from "@/components/PremiumVoucherCard";

const API = process.env.REACT_APP_BACKEND_URL;

const statusColor = {
  ACTIVE:    "bg-emerald-500/20 text-emerald-600 border-emerald-500/40",
  REDEEMED:  "bg-red-500/20 text-red-600 border-red-500/40",
  EXPIRED:   "bg-orange-500/20 text-orange-600 border-orange-500/40",
  CANCELLED: "bg-slate-500/20 text-slate-600 border-slate-500/40",
};

export default function GiftVoucherVerify({ session, onRequireLogin }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const voucherNumber = params.get("v");
  const sig = params.get("s");

  const [status, setStatus] = useState("loading");   // loading | ok | error
  const [voucher, setVoucher] = useState(null);
  const [error, setError] = useState("");

  const token = session?.token;
  const canRedeem = Boolean(session && voucher && voucher.status === "ACTIVE");

  useEffect(() => {
    if (!voucherNumber || !sig) { setStatus("error"); setError("Malformed verification link."); return; }
    if (!token) {
      // Bounce to login (parent decides)
      onRequireLogin && onRequireLogin(window.location.pathname + window.location.search);
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${API}/api/gift-voucher/verify`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token, voucher_number: voucherNumber, sig }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || "Verification failed");
        setVoucher(data.voucher);
        setStatus("ok");
      } catch (e) {
        setError(e.message || "Verification failed");
        setStatus("error");
      }
    })();
  }, [voucherNumber, sig, token, onRequireLogin]);

  if (status === "loading") {
    return <div className="p-10 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin" /><div className="mt-3 text-sm text-muted-foreground">Verifying voucher on the server…</div></div>;
  }
  if (status === "error") {
    return (
      <div className="max-w-md mx-auto p-6">
        <Card className="border-red-500/40 bg-red-500/5">
          <CardContent className="p-6 text-center">
            <ShieldAlert className="w-10 h-10 mx-auto text-red-500" />
            <div className="mt-3 font-semibold text-red-600">Voucher verification failed</div>
            <div className="text-sm text-muted-foreground mt-1">{error}</div>
            <Button variant="outline" className="mt-4" onClick={() => navigate("/")}>Go Home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const CURRENCY_SYMBOLS = {
    INR: "\u20B9", AUD: "A$", USD: "$", GBP: "\u00A3",
    EUR: "\u20AC", JPY: "\u00A5", SGD: "S$", NZD: "NZ$", CAD: "C$",
  };
  const sym = CURRENCY_SYMBOLS[(voucher.currency || "INR").toUpperCase()]
              || `${(voucher.currency || "INR").toUpperCase()} `;
  const money = `${sym}${Number(voucher.voucher_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  const redeemed = voucher.status === "REDEEMED";
  const badge = {
    ACTIVE:    { text: "ACTIVE",    tone: "from-emerald-400 to-emerald-600" },
    REDEEMED:  { text: "REDEEMED",  tone: "from-red-400 to-red-600" },
    EXPIRED:   { text: "EXPIRED",   tone: "from-orange-400 to-orange-600" },
    CANCELLED: { text: "CANCELLED", tone: "from-slate-400 to-slate-600" },
  }[voucher.status] || { text: voucher.status, tone: "from-slate-400 to-slate-600" };

  return (
    <div className="max-w-3xl mx-auto p-4 space-y-6" data-testid="verify-page">
      {/* Premium rotating voucher card */}
      <PremiumVoucherCard v={voucher} verifyUrl={window.location.href} hideTechnical />

      {/* Large premium status badge */}
      <div className="flex flex-col items-center gap-1">
        <div className={`inline-flex items-center gap-2 rounded-full px-6 py-2 text-white font-bold text-lg tracking-wider bg-gradient-to-r ${badge.tone} shadow-lg`}
             data-testid="verify-status-badge">
          <ShieldCheck className="w-5 h-5" /> {badge.text}
        </div>
        <div className="text-xs text-muted-foreground">Server-verified in real time</div>
      </div>

      {/* Customer-facing details — no technical IDs, no database jargon */}
      <Card>
        <CardContent className="p-5 sm:p-6 space-y-2 text-sm">
          <DetailRow label="Presented To"    value={voucher.recipient_name} />
          <DetailRow label="Gifted By"       value={voucher.purchaser_name} />
          {voucher.occasion && <DetailRow label="Occasion" value={voucher.occasion} />}
          {voucher.personal_message && (
            <div className="italic text-muted-foreground border-l-2 pl-3 mt-2"
                 style={{ borderColor: "#c99a3b" }}>
              &ldquo;{voucher.personal_message}&rdquo;
            </div>
          )}
          <div className="my-3 h-px bg-white/10" />
          <DetailRow label="Voucher Value" value={money} strong />
          <DetailRow label="Valid Until"   value={voucher.expires_at?.slice(0, 10)} />
        </CardContent>
      </Card>

      {redeemed && voucher.redemption && (
        <Card className="border-red-500/40 bg-red-500/5" data-testid="verify-already-redeemed">
          <CardContent className="p-4 text-sm">
            <strong>This voucher has already been redeemed.</strong>
            <div className="mt-2 text-xs space-y-1">
              <div>Redeemed on: {voucher.redemption.redeemed_at?.slice(0, 16).replace("T", " ")}</div>
              <div>At: {voucher.redemption.redeemed_center}</div>
            </div>
          </CardContent>
        </Card>
      )}

      {canRedeem && (
        <RedeemInline voucher={voucher} sig={sig} token={token}
          onDone={() => window.location.reload()} />
      )}

      <div className="text-center">
        <Button variant="outline" onClick={() => navigate("/")} className="mt-2">
          <ExternalLink className="w-4 h-4 mr-1" /> Visit Purnabramha
        </Button>
      </div>
    </div>
  );
}


function DetailRow({ label, value, strong }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-xs uppercase tracking-widest text-muted-foreground">{label}</span>
      <span className={strong ? "font-bold text-lg" : "font-medium"}>{value || "—"}</span>
    </div>
  );
}


function RedeemInline({ voucher, sig, token, onDone }) {
  const [open, setOpen] = useState(false);
  const [invoice, setInvoice] = useState("");
  const [bill, setBill] = useState(voucher.voucher_amount);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!open) {
    return (
      <div className="text-center">
        <Button className="bg-emerald-700 hover:bg-emerald-800" onClick={() => setOpen(true)}
          data-testid="verify-redeem-open">
          Redeem this voucher
        </Button>
      </div>
    );
  }

  const doRedeem = async () => {
    if (!invoice) { toast.error("Invoice # is required"); return; }
    if (!confirmed) { toast.error("Please confirm the one-time-use warning."); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/gift-voucher/redeem`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token, voucher_number: voucher.voucher_number, sig,
          center: voucher.center, invoice_number: invoice, bill_amount: bill,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Redeem failed");
      toast.success("Voucher redeemed");
      onDone && onDone();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <Card className="border-emerald-500/40">
      <CardContent className="p-4 space-y-3">
        <div className="rounded bg-amber-500/10 border border-amber-500/40 p-3 text-sm">
          This voucher will now be <strong>permanently redeemed</strong>. Continue?
        </div>
        <div>
          <label className="text-xs">Invoice # *</label>
          <input className="w-full h-9 rounded border px-2 mt-1" value={invoice}
            onChange={(e) => setInvoice(e.target.value)} data-testid="verify-redeem-invoice" />
        </div>
        <div>
          <label className="text-xs">Bill Amount</label>
          <input type="number" className="w-full h-9 rounded border px-2 mt-1" value={bill}
            onChange={(e) => setBill(parseFloat(e.target.value) || 0)} data-testid="verify-redeem-bill" />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)}
            data-testid="verify-redeem-confirm" />
          I confirm the recipient is present and this voucher will be permanently redeemed.
        </label>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setOpen(false)}>No</Button>
          <Button className="bg-emerald-700 hover:bg-emerald-800" disabled={busy} onClick={doRedeem}
            data-testid="verify-redeem-confirm">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Yes, Redeem
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
